// BaseBillingService.js

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';

export const BASE_BILLING_STATUS = Object.freeze({
  ACTIVE: 'active',
  CANCELED: 'canceled',
  GRACE_PERIOD: 'grace_period',
  INACTIVE: 'inactive',
  INCOMPLETE: 'incomplete',
  PAST_DUE: 'past_due',
  TRIALING: 'trialing',
  UNPAID: 'unpaid',
});

export const BASE_BILLING_PROVIDER = Object.freeze({
  NONE: 'none',
  STRIPE: 'stripe',
});

export const BASE_BILLING_INTERVAL = Object.freeze({
  MONTH: 'month',
  YEAR: 'year',
});

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeNullableString(value) {
  const normalized = normalizeString(value);
  return normalized || null;
}

function normalizeBoolean(value) {
  return Boolean(value);
}

function normalizeNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  const whole = Math.floor(parsed);
  if (whole < 0) return fallback;

  return whole;
}

function normalizeProvider(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === BASE_BILLING_PROVIDER.STRIPE) {
    return BASE_BILLING_PROVIDER.STRIPE;
  }

  return BASE_BILLING_PROVIDER.NONE;
}

function normalizeBillingStatus(value) {
  const normalized = normalizeString(value).toLowerCase();
  const allowedStatuses = new Set(Object.values(BASE_BILLING_STATUS));

  if (allowedStatuses.has(normalized)) {
    return normalized;
  }

  return BASE_BILLING_STATUS.INACTIVE;
}

function normalizeBillingInterval(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === BASE_BILLING_INTERVAL.YEAR) {
    return BASE_BILLING_INTERVAL.YEAR;
  }

  return BASE_BILLING_INTERVAL.MONTH;
}

function normalizeDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value?.toDate === 'function') {
    const dateValue = value.toDate();
    if (dateValue instanceof Date) {
      return dateValue;
    }
  }

  return null;
}

const MAX_BILLING_STATUS_HISTORY = 100;

function normalizeStatusHistory(input) {
  if (!Array.isArray(input)) return [];

  return input
    .map((entry) => {
      const status = normalizeBillingStatus(entry?.status);
      const changedAt = normalizeDateValue(entry?.changedAt);
      const source = normalizeNullableString(entry?.source);

      if (!status || !changedAt) return null;

      return {
        changedAt,
        source,
        status,
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.changedAt.getTime() - left.changedAt.getTime())
    .slice(0, MAX_BILLING_STATUS_HISTORY);
}

function normalizeNowValue(value) {
  if (value instanceof Date) return value;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return new Date(numericValue);
  }

  return new Date();
}

export function cleanBaseBillingRecord(docSnap) {
  const row = docSnap?.data() || {};
  const baseKey = normalizeString(row.baseKey || docSnap?.id || '');
  const billingStatus = normalizeBillingStatus(row.billingStatus || row.subscriptionStatus);
  const stripeStatus = normalizeNullableString(row.stripeStatus || row.subscriptionStatus);

  return {
    billingEmail: normalizeNullableString(row.billingEmail),
    billingName: normalizeNullableString(row.billingName),
    billingStatus,
    cancelAtPeriodEnd: normalizeBoolean(row.cancelAtPeriodEnd),
    currentPeriodEnd: normalizeDateValue(row.currentPeriodEnd),
    currentPeriodStart: normalizeDateValue(row.currentPeriodStart),
    dateCreated: normalizeDateValue(row.dateCreated),
    dateModified: normalizeDateValue(row.dateModified),
    gracePeriodEndsAt: normalizeDateValue(row.gracePeriodEndsAt),
    hasPaymentMethod: normalizeBoolean(row.hasPaymentMethod),
    id: docSnap?.id || baseKey,
    latestInvoiceId: normalizeNullableString(row.latestInvoiceId),
    ownerUserKey: normalizeNullableString(row.ownerUserKey),
    planCurrency: normalizeNullableString(row.planCurrency) || 'usd',
    planInterval: normalizeBillingInterval(row.planInterval),
    planKey: normalizeNullableString(row.planKey),
    planPriceId: normalizeNullableString(row.planPriceId || row.stripePriceId),
    planTitle: normalizeNullableString(row.planTitle),
    provider: normalizeProvider(row.provider),
    seatCount: normalizeNonNegativeInteger(row.seatCount, 0),
    seatCountUpdatedAt: normalizeDateValue(row.seatCountUpdatedAt),
    statusHistory: normalizeStatusHistory(row.statusHistory),
    stripeCustomerId: normalizeNullableString(row.stripeCustomerId),
    stripePriceId: normalizeNullableString(row.stripePriceId),
    stripeStatus,
    stripeSubscriptionId: normalizeNullableString(row.stripeSubscriptionId || row.subscriptionId),
    baseKey,
    baseTitle: normalizeNullableString(row.baseTitle),
  };
}

export function isBaseBillingInGracePeriod(record) {
  return normalizeBillingStatus(record?.billingStatus) === BASE_BILLING_STATUS.GRACE_PERIOD;
}

export function isBasePaidBillingStatus(billingStatus) {
  const normalized = normalizeBillingStatus(billingStatus);
  return normalized === BASE_BILLING_STATUS.ACTIVE
    || normalized === BASE_BILLING_STATUS.TRIALING
    || normalized === BASE_BILLING_STATUS.PAST_DUE
    || normalized === BASE_BILLING_STATUS.GRACE_PERIOD;
}

export function hasActiveBasePaidSubscription(record) {
  const subscriptionId = normalizeString(record?.stripeSubscriptionId);
  if (!subscriptionId) {
    return false;
  }

  return isBasePaidBillingStatus(record?.billingStatus);
}

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * waitForActiveBaseBilling
 *
 * Polls base billing until Stripe reports an active paid subscription state.
 *
 * @param {string} baseKey - Base key.
 * @param {object} [options] - Poll options.
 * @param {number} [options.timeoutMs] - Maximum wait time.
 * @param {number} [options.intervalMs] - Delay between polls.
 * @param {function} [options.syncFn] - Optional sync callback run before each poll.
 *
 * @returns {Promise<object>} Active base billing record.
 */
export async function waitForActiveBaseBilling(
  baseKey,
  { timeoutMs = 45000, intervalMs = 1500, syncFn } = {}
) {
  const cleanedBaseKey = normalizeString(baseKey);
  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  const startedAt = Date.now();

  while ((Date.now() - startedAt) < timeoutMs) {
    if (typeof syncFn === 'function') {
      try {
        await syncFn();
      } catch (error) {
        console.warn('[BaseBillingService] billing sync while waiting failed:', error);
      }
    }

    const record = await getBaseBillingRecord(cleanedBaseKey);
    if (isBasePaidBillingStatus(record?.billingStatus)) {
      return record;
    }

    await delay(intervalMs);
  }

  throw new Error('Payment was received, but billing is still activating. Refresh plan settings in a moment.');
}

export function isBaseBillingNavigationLocked(record, now = new Date()) {
  if (!isBaseBillingInGracePeriod(record)) {
    return false;
  }

  const gracePeriodEndsAt = normalizeDateValue(record?.gracePeriodEndsAt);
  if (!gracePeriodEndsAt) {
    return false;
  }

  return gracePeriodEndsAt.getTime() <= normalizeNowValue(now).getTime();
}

export function baseBypassesTrialLock(base, plan = null) {
  if (base?.planBypassesTrialLock === true) {
    return true;
  }

  return plan?.bypassTrialLock === true;
}

export function isBaseTrialAccessLocked(base, billingRecord, now = new Date(), plan = null) {
  if (baseBypassesTrialLock(base, plan)) {
    return false;
  }

  if (hasActiveBasePaidSubscription(billingRecord)) {
    return false;
  }

  if (base?.trialExpired === true) {
    return true;
  }

  const trialEndsAt = normalizeDateValue(base?.trialEndsAt);
  if (!trialEndsAt) {
    return false;
  }

  return trialEndsAt.getTime() <= normalizeNowValue(now).getTime();
}

export function getBaseLockReason(base, billingRecord, now = new Date(), plan = null) {
  if (isBaseBillingNavigationLocked(billingRecord, now)) {
    return 'billing_grace_expired';
  }

  if (isBaseTrialAccessLocked(base, billingRecord, now, plan)) {
    return 'trial_expired';
  }

  return null;
}

export function isBaseAccessLocked(base, billingRecord, now = new Date(), plan = null) {
  return Boolean(getBaseLockReason(base, billingRecord, now, plan));
}

export function computeBaseTrialDaysRemaining(base, now = new Date(), plan = null) {
  if (baseBypassesTrialLock(base, plan)) {
    return null;
  }
  const trialEndsAt = normalizeDateValue(base?.trialEndsAt);
  if (!trialEndsAt) {
    return null;
  }

  const nowDate = normalizeNowValue(now);
  const diffMs = trialEndsAt.getTime() - nowDate.getTime();
  if (diffMs <= 0) {
    return 0;
  }

  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

export async function getBaseBillingRecord(baseKey) {
  const cleanedBaseKey = normalizeString(baseKey);

  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  const docSnap = await getDoc(doc(db, collections.baseBilling, cleanedBaseKey));
  if (!docSnap.exists()) {
    return null;
  }

  return cleanBaseBillingRecord(docSnap);
}

export function subscribeToBaseBillingRecord(baseKey, callback, onError) {
  const cleanedBaseKey = normalizeString(baseKey);

  if (!cleanedBaseKey) {
    callback && callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, collections.baseBilling, cleanedBaseKey),
    (docSnap) => {
      callback && callback(docSnap.exists() ? cleanBaseBillingRecord(docSnap) : null);
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
        return;
      }

      console.error('[BaseBillingService] subscribeToBaseBillingRecord error:', error);
    }
  );
}

export async function upsertBaseBillingRecord(baseKey, patch = {}) {
  const cleanedBaseKey = normalizeString(baseKey);

  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
    throw new Error('patch object is required');
  }

  const payload = {
    ...patch,
    baseKey: cleanedBaseKey,
    dateModified: serverTimestamp(),
  };

  if (!Object.prototype.hasOwnProperty.call(payload, 'dateCreated')) {
    payload.dateCreated = serverTimestamp();
  }

  await setDoc(doc(db, collections.baseBilling, cleanedBaseKey), payload, {
    merge: true,
  });
}